// Small pure helpers shared by the chat window and the sidebar modals.

// Model option label for the current UI language. The vehicle data carries
// `valueInJap` / `valueInEng`, so building the key as `valueIn${langOpt}`
// produced "valueInJapanese" — never present — and Japanese users silently
// fell back to English labels. Languages without their own label use English.
export const modelLabel = (model, langOpt) => {
  if (!model) return '';
  if (langOpt === 'Japanese') return model.valueInJap || model.valueInEng || '';
  return model.valueInEng || model.valueInJap || '';
};

// Case IDs in the order the backend ranked them (`_sorted_order` when present).
export const orderedCaseIds = (solutions) => {
  if (!solutions || typeof solutions !== 'object') return [];
  const order = Array.isArray(solutions._sorted_order)
    ? solutions._sorted_order
    : Object.keys(solutions);
  return order.filter((caseId) => caseId !== '_sorted_order');
};

// Identifier for one assistant answer, so a vote is tied to that answer and
// the backend can dedupe repeated submissions for it.
export const makeMessageId = () => {
  try {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
  } catch (_) { }
  return `msg-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

// Manual page references (name + page) shown with an answer — the images
// themselves are far too large to send with a vote.
export const manualRefs = (manuals) => (Array.isArray(manuals) ? manuals : [])
  .filter((manual) => manual && (manual.manual_name || manual.page_number))
  .map((manual) => `${manual.manual_name || ''}#${manual.page_number ?? ''}`);

// Cheap content key for a base64 image: length plus head/middle/tail slices.
// Distinct page renders practically never collide on all of these, and it
// avoids hashing megabytes of base64 on every render.
const imageKey = (b64) => {
  const mid = Math.floor(b64.length / 2);
  return `${b64.length}:${b64.slice(0, 64)}:${b64.slice(mid, mid + 64)}:${b64.slice(-64)}`;
};

// Safety net against the same manual page arriving more than once: drops
// entries with identical image content or the same (manual_name, page_number).
// Entries without an image are dropped too — they render nothing, and keeping
// one could shadow a later copy of the same page that does have its image.
export const dedupeManuals = (manuals) => {
  if (!Array.isArray(manuals)) return [];
  const seenImages = new Set();
  const seenPages = new Set();
  return manuals.filter((manual) => {
    const b64 = manual && typeof manual.base64_image === 'string' ? manual.base64_image : '';
    if (!b64) return false;
    const key = imageKey(b64);
    if (seenImages.has(key)) return false;
    seenImages.add(key);
    if (manual.manual_name && manual.page_number !== undefined && manual.page_number !== null) {
      const pageKey = `${manual.manual_name}#${manual.page_number}`;
      if (seenPages.has(pageKey)) return false;
      seenPages.add(pageKey);
    }
    return true;
  });
};

// Same idea for the plain base64 image strip.
export const dedupeImages = (images) => {
  if (!Array.isArray(images)) return [];
  const seen = new Set();
  return images.filter((b64) => {
    if (typeof b64 !== 'string' || !b64) return false;
    const key = imageKey(b64);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

// POST to /chat in streaming mode, reporting the turn as it happens: onStatus
// with each step, onDelta with each piece of answer text, onCases once the case
// panels are ready (case turns), onAnswer once the text is final (manual turns;
// pages are still loading). Resolves with the normal /chat body when the turn
// has finished. A proxy that strips streaming still works: the whole body then
// arrives at once.
export const postChatStream = async (url, body, { onDelta, onAnswer, onStatus, onCases }) => {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...body, stream: true }),
  });
  if (!response.ok || !response.body) {
    throw new Error(`Chat request failed with status ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffered = '';
  let finished = null;

  const handleLine = (line) => {
    if (!line.trim()) return;
    const event = JSON.parse(line);
    if (event.type === 'delta') onDelta(event.text);
    if (event.type === 'answer') onAnswer(event.data);
    if (event.type === 'status') onStatus(event.label);
    if (event.type === 'cases') onCases(event.data);
    if (event.type === 'done') finished = event;
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffered += decoder.decode(value, { stream: true });
    const lines = buffered.split('\n');
    buffered = lines.pop();
    lines.forEach(handleLine);
  }
  handleLine(buffered);

  if (!finished) throw new Error('The chat stream ended before the answer finished');
  if (finished.status >= 400 || !finished.data || typeof finished.data.response !== 'string') {
    throw new Error(finished.data?.error || finished.data?.message || `Chat failed with status ${finished.status}`);
  }
  return finished.data;
};
